// What the disk screen says, from what the town sends (sim world/disks/Screen.js), kept apart from the component so it
// can be read and tested without a page (components/ui/DiskTerminal.jsx).
//
// The town sends each step of a sitting once, with only what is new: `in` (what is on the disk, his change if the disk
// takes it, how often he has run it), `ran` (what it printed, or that it made a picture), `done` (the link, if it went
// up) and `broken` (why it would not run). foldDisk puts them together; linesOf says them, every line something the
// town says happened or something that is on the disk. Nothing here makes anything up.

/** The screen so far, with one more step folded in. A step for a sitting we never saw open is marked to catch up. */
export function foldDisk(prev, msg) {
  if (!msg?.id || !msg.phase) return prev
  const { type, ...step } = msg
  if (step.phase === 'in') return { ...step }
  if (prev?.id === step.id) return { ...prev, ...step }
  return { ...step, partial: true }
}

const KIND = { page: 'a moving picture', script: 'a program that prints' }
const CODE_LINES = 12
const PRINTED_LINES = 80
const n = (x) => Number(x).toLocaleString('en-GB')

/**
 * The lines on the screen, in order: { k, t } where k is 'him' (what he did), 'cmd' (at the prompt), 'out', 'what' (what
 * the disk is), 'code', 'more', 'pic', 'link', 'err'. `name` is the bird's; `left` is true once he has got up.
 */
export function linesOf(s, { name = 'Pino', left = false } = {}) {
  if (!s || s.partial) return []
  const out = []
  const add = (k, t = '') => out.push({ k, t })
  add('him', `${name} put DISK ${s.no} / ${s.label} in the drive${s.times ? ' again' : ''}`)
  if (s.change) add('him', `with one change: ${s.change}`)
  add('cmd', 'dir')
  if (s.closed) add('out', 'the disk would not open. only what it prints can be read')
  else add('out', `one program, ${n(s.bytes)} bytes, ${KIND[s.kind] || 'a program'}`)
  if (s.what) add('what', s.what)
  if (s.changes?.length) add('out', `it can be changed: ${s.changes.join(', ')}`)
  if (s.code) {
    add('cmd', 'type')
    const code = s.code.split('\n')
    for (const l of code.slice(0, CODE_LINES)) add('code', l)
    if (code.length > CODE_LINES) add('more', `… ${n(code.length - CODE_LINES)} more lines`)
  }
  if (s.phase === 'ran' || s.phase === 'done' || s.phase === 'broken') {
    add('cmd', `run${s.change ? `, with ${s.change}` : ''}`)
    if (s.phase === 'broken') add('err', `it would not run: ${s.error || 'no reason given'}`)
    else {
      if (s.picture) add('pic', 'four moments of it running')
      if (s.printed) {
        const printed = s.printed.split('\n')
        for (const l of printed.slice(0, PRINTED_LINES)) add('out', l)
        if (printed.length > PRINTED_LINES) add('more', `… ${n(printed.length - PRINTED_LINES)} more lines`)
      }
      if (s.mended) add('him', 'it was mended with what DISK 05 left on this computer')
      if (s.phase === 'done' && s.url) add('link', 'it went up on his code site')
    }
  }
  if (left) add('more', `[${name.toLowerCase()} got up]`)
  return out
}

/** The word in the title bar, and its colour (checked on the cream bar, as the browser window's are). */
export function statusOf(s, { left = false } = {}) {
  if (!s) return { label: '', color: '#57506e' }
  if (s.phase === 'broken') return { label: 'would not run', color: '#a83832' }
  if (left && s.phase === 'in') return { label: 'got up', color: '#57506e' }
  if (s.phase === 'in') return { label: 'at the computer', color: '#175f83' }
  if (s.phase === 'ran') return { label: 'it ran', color: '#276b3c' }
  return { label: 'done', color: '#276b3c' }
}

/** Over: nothing more will come of this sitting. */
export const overOf = (s) => !!s && (s.phase === 'done' || s.phase === 'broken')

/**
 * Whether a page should ask the town for the sitting as it stands (GET /api/disk-screen): 'now' for a step of a sitting
 * it never saw open; 'soon' when he has just sat down at the drive (`sat`) and no disk has come in, asked after a moment
 * in case it is on its way; otherwise null. The steps of a run land while he is still sat there, so the end of a sitting
 * is never a reason to ask: with fifty watching, that was fifty requests a run.
 */
export function catchUpOf(screen, { sat = false } = {}) {
  if (screen?.partial) return 'now'
  if (sat && screen?.phase !== 'in') return 'soon'
  return null
}

/** After the moment: still missing if no disk came in and no other sitting began since `before` (its id, or null). */
export const stillMissing = (now, before) => now?.phase !== 'in' && (now?.id || null) === before
